import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import DogPlaceholderSvg from 'dpl/common/components/DogPlaceholderSvg';
import Icon from 'dpl/common/components/Icon';
import SmartImage from 'dpl/components/SmartImage';
import { PAYMENT_REQUEST_CATEGORIES } from 'dpl/shared/constants/breeder_app';
import useToggle from 'dpl/shared/hooks/useToggle';
import FloatingTooltipWrapper from 'dpl/common/tooltip/components/FloatingTooltipWrapper';

import {
  ensureCurrencyIsPresent,
  getDepositRefundPolicyAdjective,
  getDepositSecuresText,
  getFinalPaymentTimingText
} from './util';

/** @param {any} props */
export default function StripeChargeModalPuppyCardV2({
  className = null,
  dog = null,
  litter = null,
  amountInDollars = null,
  paymentCategory = PAYMENT_REQUEST_CATEGORIES.DOG_PAYMENT,
  breederProfile,
  dogDescription = null,
  onEditClick = null
}) {
  const photoSize = '48px';
  const totalWithDollar = ensureCurrencyIsPresent(amountInDollars);
  const breedName = dog?.breed?.name || dog?.breed_name;
  const isDeposit = paymentCategory === PAYMENT_REQUEST_CATEGORIES.DEPOSIT;
  const paymentText = isDeposit ? 'Deposit' : 'Payment';
  const titleText = `${paymentText} for ${dog?.name ?? 'dog'}`;

  // deposit_refund_policy is force-synced to active litters, so litter vs
  // program is moot there. deposit_description is the exception: the litter
  // keeps its own optional override, which is blank far more often than not,
  // so it must fall through to the breeder's value rather than blanking it.
  const depositRefundPolicy =
    litter?.deposit_refund_policy || breederProfile.deposit_refund_policy;
  const depositDescription =
    litter?.deposit_description || breederProfile.deposit_description;

  // deposit_secures and final_payment_timing are program-level only: the
  // litter columns are orphaned/nonexistent, so these never read from litter.
  const depositSecures = breederProfile.deposit_secures;
  const finalPaymentTiming = breederProfile.final_payment_timing;

  function renderSubtitle() {
    if (isDeposit) {
      const refundPolicyAdjective =
        getDepositRefundPolicyAdjective(depositRefundPolicy);
      const securesText = getDepositSecuresText(depositSecures, dog?.name);
      const timingText = getFinalPaymentTimingText(finalPaymentTiming);

      const summarySentence = `Your ${totalWithDollar} ${
        refundPolicyAdjective ? `${refundPolicyAdjective} ` : ''
      }deposit${securesText ? ` reserves ${securesText}` : ''}.`;

      return (
        <>
          <p>{summarySentence}</p>
          {timingText && <p>{`The remaining balance is due ${timingText}.`}</p>}
          {depositDescription && (
            <BreederPolicyDisclosure description={depositDescription} />
          )}
        </>
      );
    }

    if (paymentCategory === PAYMENT_REQUEST_CATEGORIES.DOG_PAYMENT) {
      if (dogDescription) {
        return <p>{dogDescription}</p>;
      }

      if (breedName) {
        return <p>{breedName} puppy</p>;
      }

      if (litter?.name) {
        return <p>From {litter.name}&apos;s litter</p>;
      }

      return <p>From {breederProfile.name}</p>;
    }

    return null;
  }

  return (
    <div className={classnames('StripeChargeModalPuppyCardV2', className)}>
      <div className="row no-gutters items-start">
        <div className="col col-sm-auto mb-1 mb0-sm">
          <div className="f0 mr3">
            {dog?.profile_photo_url ? (
              <SmartImage
                src={dog?.profile_photo_url}
                width={photoSize}
                height={photoSize}
                className="br-100 bg-white"
                loadingClass="bg-near-white"
                crop
                lazy
              />
            ) : (
              <DogPlaceholderSvg
                className="br-100 bg-white"
                width={photoSize}
                height={photoSize}
                icon
              />
            )}
          </div>
        </div>
        <div className="col-12 col-sm order-last order-sm-0">
          <div className="row no-gutters items-start">
            <div className="col-12 col-sm">
              <p className="font-serif font-18 md:font-20">{titleText}</p>
              <div className="font-14 secondary">{renderSubtitle()}</div>
            </div>
            {amountInDollars > 0 && (
              <div className="f0 col-12 col-sm-auto mt-2 mt-sm-1">
                <AmountPill
                  value={totalWithDollar}
                  category={paymentCategory}
                />
              </div>
            )}
          </div>
        </div>
        {onEditClick && (
          <div className="col-auto self-start">
            <button
              type="button"
              className="bg-white pv2 ph2 br-100 b--gray-300 ba ml2"
              onClick={onEditClick}
            >
              <Icon
                name="fetch-edit-alt"
                className="stone-700"
                width="20px"
                height="20px"
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

StripeChargeModalPuppyCardV2.propTypes = {
  className: PropTypes.string,
  breederProfile: PropTypes.shape({
    profile_photo_url: PropTypes.string,
    name: PropTypes.string.isRequired,
    pay_url: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deposit_refund_policy: PropTypes.string,
    deposit_description: PropTypes.string,
    deposit_secures: PropTypes.string,
    final_payment_timing: PropTypes.string
  }).isRequired,
  amountInDollars: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dog: PropTypes.shape({
    name: PropTypes.string.isRequired,
    profile_photo_url: PropTypes.string,
    breed_name: PropTypes.string,
    breed: PropTypes.shape({
      name: PropTypes.string
    })
  }),
  litter: PropTypes.shape({
    name: PropTypes.string,
    deposit_refund_policy: PropTypes.string,
    deposit_description: PropTypes.string
  }),
  dogDescription: PropTypes.string,
  paymentCategory: PropTypes.oneOf(Object.values(PAYMENT_REQUEST_CATEGORIES)),
  onEditClick: PropTypes.func
};

function BreederPolicyDisclosure({ description }) {
  const [isExpanded, toggleIsExpanded] = useToggle(false);

  return (
    <div>
      <button
        type="button"
        className="inline-flex items-center gap-4 underline mt1"
        aria-expanded={isExpanded}
        onClick={() => toggleIsExpanded()}
      >
        <span>Breeder&apos;s policy</span>
        <Icon
          name={isExpanded ? 'fetch-chevron-up' : 'fetch-chevron-down'}
          width="12px"
          height="12px"
        />
      </button>
      {isExpanded && <p className="mt1">{description}</p>}
    </div>
  );
}

BreederPolicyDisclosure.propTypes = {
  description: PropTypes.string.isRequired
};

function AmountPill({ value, category }) {
  const preTaxLabel =
    category === PAYMENT_REQUEST_CATEGORIES.DOG_PAYMENT
      ? 'Before taxes & fees, calculated below'
      : 'Before fees, calculated below';

  return (
    <div className="AmountPill inline-flex items-center gap-6 br-pill bg-stone-700 font-14 lh-copy white pv1 ph3">
      <span>{value}</span>
      <FloatingTooltipWrapper
        tooltipTextClassName="pt18 pb10 ph5"
        title={preTaxLabel}
        minWidth="200px"
      >
        <Icon className="nr1" name="fetch-info" height="20px" width="20px" />
      </FloatingTooltipWrapper>
    </div>
  );
}

AmountPill.propTypes = {
  value: PropTypes.string.isRequired,
  category: PropTypes.oneOf(Object.values(PAYMENT_REQUEST_CATEGORIES))
    .isRequired
};
